import { supabase } from '@/integrations/supabase/client';
import { CampaignStateMachine } from './stateMachines/CampaignStateMachine';
import { AuditService } from './AuditService';

import { ErrorHandler } from './ErrorHandler';
import { CacheService } from './index';
import { CampaignStatus, ReferentStatus } from '@/types/states';
import type { Campaign } from '@/types/unified';
import {
    StateTransitionError,
    BusinessRuleViolationError,
    PermissionDeniedError,
    ValidationError
} from '@/errors/BusinessErrors';
import { validateContextAndExecute } from '@/lib/contextAwareService';
import { logger } from '@/utils/productionLogger';
import { cacheHelpers, performanceCache, CACHE_TYPES } from './performanceCache';

export interface CampaignUpdateData {
    status?: CampaignStatus;
    title?: string;
    description?: string;
    evaluation_criteria?: string[];
}

export interface CandidateResponseStatus {
    status: string;
    campaign_id: string;
    candidate_name: string;
    target_position: string;
    response_type?: string;
    refusal_reason?: string;
    submitted_at?: string;
}

export class CampaignService {
    private auditService: AuditService;

    private listenersInitialized = false;

    constructor() {
        this.auditService = new AuditService();

        // Initialiser les listeners d'événements
        this.initializeEventListeners();
    }

    /**
     * Initialise les listeners d'événements
     * CRITIQUE : Doit être appelé avant toute émission d'événements
     */
    private initializeEventListeners(): void {
        if (this.listenersInitialized) return;

        try {
            // Import différé pour éviter les cycles
            import('@/lib/EventBus').then(({ eventBus }) => {
                // Écouter les changements de statut des référents
                eventBus.on('referent-status-changed', async (data: any) => {
                    try {
                        logger.info('[Event] referent-status-changed received', data);
                        await this.autoProgressCampaignDirect(data.campaignId);
                    } catch (_error) {
                        logger.error('[Event] Error in referent-status-changed handler', _error as Error);
                    }
                });

                this.listenersInitialized = true;
                logger.info('[EventBus] CampaignService listeners initialized');
            }).catch((error) => {
                logger.warn('[EventBus] Failed to initialize listeners, continuing without events', error as Error);
            });
        } catch (_error) {
            logger.warn('[EventBus] Failed to initialize listeners, continuing without events', _error as Error);
        }
    }

    /**
     * Récupère une campagne par son ID avec validation des permissions et cache
     */
    async getCampaign(campaignId: string, organizationId?: string): Promise<Campaign> {
        // Essayer de récupérer depuis le cache si on a l'organizationId
        if (organizationId) {
            // PHASE 2: Utiliser le nouveau CacheService
            const cacheKey = CacheService.generateKey('campaign', campaignId);
            const cached = CacheService.get<Campaign>(cacheKey);
            if (cached) return cached;
            
            // OPTIMISÉ: Méthode directe avec cache intelligent
            const result = await this.fetchCampaignFromDB(campaignId);
            CacheService.set(cacheKey, result, 10 * 60 * 1000); // 10min TTL pour les campagnes
            return result;
        }

        // Sinon, récupération directe
        return await this.fetchCampaignFromDB(campaignId);
    }

    /**
     * Récupère une campagne directement depuis la base de données
     */
    private async fetchCampaignFromDB(campaignId: string): Promise<Campaign> {
        const { data, error } = await supabase
            .from('campaigns')
            .select(`
          *,
          referents (*)
        `)
            .eq('id', campaignId)
            .single();

        if (error) {
            throw ErrorHandler.handleDatabaseError(error);
        }

        if (!data) {
            throw new ValidationError('Campagne introuvable');
        }

        return data as unknown as Campaign;
    }

    /**
     * Récupère une campagne par son token public
     * @deprecated Use UnifiedCampaignService.getCampaignData instead
     */
    async getCampaignByToken(token: string): Promise<Campaign> {
        logger.warn('getCampaignByToken is deprecated - use UnifiedCampaignService.getCampaignData instead');
        logger.debug('Récupération campagne avec token', { token });

        return await validateContextAndExecute(async () => {
            const { data: campaign, error: campaignError } = await supabase
                .rpc('get_campaign_by_token', { p_token: token })
                .maybeSingle();

            if (campaignError) {
                logger.error('Erreur Supabase lors de la récupération de campagne', campaignError, { token });
                throw ErrorHandler.handleDatabaseError(campaignError);
            }

            if (!campaign) {
                logger.warn('Aucune campagne trouvée pour le token', { token });
                throw new ValidationError('Lien invalide ou expiré. Vérifiez que le lien n\'a pas été modifié.');
            }

            logger.debug('Campagne trouvée', {
                campaignId: campaign.id,
                status: campaign.status,
                candidate_name: campaign.candidate_name
            });

            // Récupérer les référents via RPC sécurisée liée au token
            const { data: referents, error: refErr } = await supabase
                .rpc('get_referents_by_campaign_token', { p_token: token });

            if (refErr) {
                logger.warn('Erreur récupération référents via RPC', refErr);
            }

            logger.debug('Référents récupérés', { count: referents?.length || 0 });

            return {
                ...campaign,
                referents: referents || []
            } as unknown as Campaign;
        }, 'campaign');
    }

    /**
     * Récupère une campagne par son token public sans validation de contexte
     * Version moderne qui ne dépend pas du système de contexte obsolète
     * OPTIMISÉ avec cache pour éviter les rechargements inutiles
     */
    async getCampaignByTokenDirect(token: string): Promise<Campaign> {
        logger.debug('Récupération campagne avec token (direct + cache)', { token });

        // OPTIMISATION: Vérifier le cache d'abord
        const cachedCampaign = cacheHelpers.getCampaignData<Campaign>(token);
        if (cachedCampaign) {
            logger.debug('🚀 Campagne récupérée depuis le cache');
            return cachedCampaign;
        }

        const { data: campaign, error: campaignError } = await supabase
            .rpc('get_campaign_by_token', { p_token: token })
            .maybeSingle();

        if (campaignError) {
            logger.error('Erreur Supabase lors de la récupération de campagne', campaignError, { token });
            throw ErrorHandler.handleDatabaseError(campaignError);
        }

        if (!campaign) {
            logger.warn('Aucune campagne trouvée pour le token', { token });
            throw new ValidationError('Lien invalide ou expiré. Vérifiez que le lien n\'a pas été modifié.');
        }

        logger.debug('Campagne trouvée', {
            campaignId: campaign.id,
            status: campaign.status,
            candidate_name: campaign.candidate_name
        });

        // Récupérer les référents via RPC sécurisée liée au token
        const { data: referents, error: refErr } = await supabase
            .rpc('get_referents_by_campaign_token', { p_token: token });

        if (refErr) {
            logger.warn('Erreur récupération référents via RPC', refErr);
        }

        logger.debug('Référents récupérés', { count: referents?.length || 0 });

        const result = {
            ...campaign,
            referents: referents || []
        } as unknown as Campaign;

        // OPTIMISATION: Mettre en cache pour les prochaines visites
        cacheHelpers.setCampaignData(token, result);
        logger.debug('💾 Campagne mise en cache');

        return result;
    }

    /**
     * Met à jour le statut d'une campagne avec validation des transitions et invalidation du cache
     */
    async updateCampaignStatus(
        campaignId: string,
        newStatus: CampaignStatus,
        reason?: string,
        userId?: string
    ): Promise<Campaign> {
        return await validateContextAndExecute(async () => {
            // 1. Validation des permissions
            await this.validatePermissions(campaignId, userId);

            // 2. Récupération de l'état actuel
            const campaign = await this.getCampaign(campaignId);
            const currentStatus = campaign.status as CampaignStatus;

            // 3. Validation de la transition
            if (!CampaignStateMachine.canTransition(currentStatus, newStatus)) {
                throw new StateTransitionError(
                    currentStatus,
                    newStatus,
                    'campaign',
                    { campaignId }
                );
            }

            // 4. Validation des règles métier
            await this.validateBusinessRules(campaign, newStatus);

            // 5. Mise à jour avec audit
            const updatedCampaign = await this.updateWithAudit(
                campaignId,
                { status: newStatus },
                reason,
                userId,
                currentStatus
            );

            // 6. Invalidation du cache pour cette campagne
            CacheService.invalidateCampaign(campaignId);

            // 7. Déclenchement des événements
            await this.triggerStatusChangeEvents(updatedCampaign, currentStatus);

            return updatedCampaign;
        }, 'campaign');
    }

    /**
     * Valide qu'une campagne peut être activée (passage de Brouillon à En attente candidat)
     */
    async validateCampaignActivation(campaignId: string): Promise<{ valid: boolean; errors: string[] }> {
        const errors: string[] = [];

        try {
            const campaign = await this.getCampaign(campaignId);

            // Vérifier les critères d'évaluation
            if (!campaign.evaluation_criteria || campaign.evaluation_criteria.length < 5) {
                errors.push('Au moins 5 critères d\'évaluation sont requis');
            }

            if (campaign.evaluation_criteria && campaign.evaluation_criteria.length > 10) {
                errors.push('Maximum 10 critères d\'évaluation autorisés');
            }

            // Vérifier les informations de base
            if (!campaign.title?.trim()) {
                errors.push('Le titre de la campagne est requis');
            }

            if (!campaign.description?.trim()) {
                errors.push('La description de la campagne est requise');
            }

            // Vérifier les informations du candidat
            if (!campaign.candidate_name?.trim()) {
                errors.push('Le nom du candidat est requis');
            }

            if (!campaign.candidate_email?.trim()) {
                errors.push('L\'email du candidat est requis');
            }

            return {
                valid: errors.length === 0,
                errors
            };
        } catch (_error) {
            return {
                valid: false,
                errors: ['Erreur lors de la validation de la campagne']
            };
        }
    }

    /**
     * Progression automatique des campagnes selon les règles métier
     * @deprecated Use autoProgressCampaignDirect for context-free operations
     */
    async autoProgressCampaign(campaignId: string, userId?: string): Promise<void> {
        logger.warn('autoProgressCampaign is deprecated - use autoProgressCampaignDirect instead');
        return await validateContextAndExecute(async () => {
            const campaign = await this.getCampaign(campaignId);
            const currentStatus = campaign.status as CampaignStatus;

            logger.debug('Vérification progression automatique', { campaignId, currentStatus });

            if (currentStatus === CampaignStatus.EN_ATTENTE_REFERENTS) {
                const allReferentsCompleted = await this.checkAllReferentsCompleted(campaignId);

                if (allReferentsCompleted) {
                    logger.campaignAction('progression_auto', campaignId, { newStatus: CampaignStatus.TERMINE });
                    await this.updateCampaignStatus(
                        campaignId,
                        CampaignStatus.TERMINE,
                        'Tous les référents ont terminé leurs évaluations',
                        userId
                    );
                }
            }
        }, 'campaign');
    }

    /**
     * Progression automatique des campagnes sans validation de contexte
     * Version moderne qui ne dépend pas du système de contexte obsolète
     */
    async autoProgressCampaignDirect(campaignId: string, userId?: string): Promise<void> {
        logger.debug('Début progression automatique campagne', { campaignId });

        try {
            // 1. Récupérer la campagne directement (sans contexte)
            const { data: campaign, error: campaignError } = await supabase
                .from('campaigns')
                .select('*')
                .eq('id', campaignId)
                .single();

            if (campaignError || !campaign) {
                logger.error('Campagne introuvable pour progression', campaignError);
                return;
            }

            // 2. Vérifier si la campagne peut progresser
            if (campaign.status === 'Terminé' || campaign.status === 'Annulée') {
                logger.debug('Campagne déjà terminée ou annulée', { campaignId, status: campaign.status });
                return;
            }

            // 3. Récupérer tous les référents de la campagne
            const { data: referents, error: referentsError } = await supabase
                .from('referents')
                .select('*')
                .eq('campaign_id', campaignId);

            if (referentsError) {
                logger.error('Erreur récupération référents', referentsError);
                return;
            }

            if (!referents || referents.length === 0) {
                logger.debug('Aucun référent pour cette campagne', { campaignId });
                return;
            }

            // 4. Vérifier si tous les référents ont complété leur évaluation
            const completedReferents = referents.filter(r => r.status === 'Complété');
            const allCompleted = completedReferents.length === referents.length;

            logger.debug('État des référents', {
                campaignId,
                total: referents.length,
                completed: completedReferents.length,
                allCompleted
            });

            // 5. Si tous complétés, passer la campagne en "Terminé"
            if (allCompleted && campaign.status !== 'Terminé') {
                const { error: updateError } = await supabase
                    .from('campaigns')
                    .update({
                        status: 'Terminé',
                        updated_at: new Date().toISOString()
                    })
                    .eq('id', campaignId);

                if (updateError) {
                    logger.error('Erreur mise à jour statut campagne', updateError);
                    return;
                }

                logger.info('Campagne automatiquement terminée', { campaignId });

                // 6. Déclencher la génération de rapport
                await this.triggerReportGeneration(campaignId);

                // 7. Créer une notification
                await this.createCompletionNotification(campaignId, userId);
            }

        } catch (_error) {
            logger.error('Erreur dans autoProgressCampaignDirect', _error as Error);
        }
    }

    /**
     * Déclenche la génération de rapport pour une campagne terminée
     */
    private async triggerReportGeneration(campaignId: string): Promise<void> {
        try {
            logger.debug('Déclenchement génération rapport', { campaignId });

            // Utiliser un appel HTTP direct avec la service role key pour éviter les problèmes d'authentification
            const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
            // ATTENTION: Ne jamais exposer la service role key côté client !
            // Cette approche nécessite une fonction intermédiaire sécurisée
            const serviceRoleKey = 'PLACEHOLDER_FOR_SECURE_IMPLEMENTATION';
            
            if (!supabaseUrl || !serviceRoleKey) {
                logger.error('Variables d\'environnement manquantes pour la génération de rapport');
                return;
            }

            // Utiliser la fonction proxy sécurisée au lieu d'un appel direct
            const { data, error } = await supabase.functions.invoke('trigger-report-generation', {
                body: { campaignId }
            });

            if (error) {
                throw new Error(`Erreur génération rapport: ${error.message}`);
            }
            logger.info('Rapport généré avec succès', { campaignId, reportId: data?.reportId });
        } catch (_error) {
            logger.error('Erreur déclenchement génération rapport', _error as Error);
            
            // Fallback : créer une notification pour informer l'utilisateur
            try {
                await supabase
                    .from('activity_notifications')
                    .insert({
                        type: 'report_generation_triggered',
                        message: `Génération de rapport échouée pour la campagne ${campaignId}. Veuillez générer le rapport manuellement.`,
                        campaign_id: campaignId,
                        recruiter_id: (await supabase.from('campaigns').select('recruiter_id').eq('id', campaignId).single()).data?.recruiter_id,
                        is_read: false
                    });
            } catch (notifError) {
                logger.error('Erreur création notification fallback', notifError as Error);
            }
        }
    }

    /**
     * Crée une notification de completion de campagne
     */
    private async createCompletionNotification(campaignId: string, _userId?: string): Promise<void> {
        try {
            // Récupérer les infos de la campagne pour la notification
            const { data: campaign } = await supabase
                .from('campaigns')
                .select('candidate_name, recruiter_id')
                .eq('id', campaignId)
                .single();

            if (!campaign) return;

            const { error } = await supabase
                .from('activity_notifications')
                .insert({
                    recruiter_id: campaign.recruiter_id,
                    campaign_id: campaignId,
                    type: 'report_generated',
                    message: `Le rapport pour ${campaign.candidate_name} est prêt`,
                    is_read: false
                });

            if (error) {
                logger.error('Erreur création notification', error);
            } else {
                logger.info('Notification créée', { campaignId });
            }
        } catch (_error) {
            logger.error('Erreur création notification', _error as Error);
        }
    }

    /**
     * Acceptation d'une campagne par le candidat
     */
    async acceptCampaign(campaignToken: string): Promise<void> {
        logger.campaignAction('acceptation_candidat', campaignToken);

        try {
            const { data, error } = await supabase.functions.invoke('submit-candidate-response', {
                body: {
                    campaign_token: campaignToken,
                    response_type: 'accept',
                    refusal_reason: null
                }
            });

            if (error) {
                logger.error('Erreur Edge Function acceptation', error, { campaignToken });
                throw ErrorHandler.handleEdgeFunctionError(error);
            }

            if (data.already_submitted) {
                logger.debug('Acceptation déjà enregistrée', { campaignToken });
            } else {
                logger.info('Acceptation enregistrée avec succès', { campaignToken });

                // Déclencher la progression automatique (version sans contexte)
                if (data.campaign_id) {
                    await this.autoProgressCampaignDirect(data.campaign_id);
                }
            }
        } catch (error: any) {
            logger.error('Erreur dans acceptCampaign', error, { campaignToken });
            throw error;
        }
    }

    /**
     * Refus d'une campagne par le candidat
     */
    async refuseCampaign(campaignToken: string, reason: string): Promise<void> {
        logger.campaignAction('refus_candidat', campaignToken, { reason });

        try {
            const { data, error } = await supabase.functions.invoke('submit-candidate-response', {
                body: {
                    campaign_token: campaignToken,
                    response_type: 'refuse',
                    refusal_reason: reason
                }
            });

            if (error) {
                logger.error('Erreur Edge Function refus', error, { campaignToken });
                throw ErrorHandler.handleEdgeFunctionError(error);
            }

            if (data.already_submitted) {
                logger.debug('Refus déjà enregistré', { campaignToken });
            } else {
                logger.info('Refus enregistré avec succès', { campaignToken });

                // Le statut est mis à jour automatiquement par l'Edge Function
                logger.debug('Statut mis à jour par Edge Function', { campaignId: data.campaign_id });
            }
        } catch (error: any) {
            logger.error('Erreur dans refuseCampaign', error, { campaignToken });
            throw error;
        }
    }

    /**
     * Annulation de la réponse du candidat
     */
    async cancelCandidateResponse(campaignToken: string): Promise<void> {
        logger.campaignAction('annulation_reponse', campaignToken);

        try {
            const { error } = await supabase.functions.invoke('cancel-candidate-response', {
                body: {
                    campaign_token: campaignToken
                }
            });

            if (error) {
                logger.error('Erreur Edge Function annulation', error, { campaignToken });
                throw ErrorHandler.handleEdgeFunctionError(error);
            }

            logger.info('Réponse annulée avec succès', { campaignToken });
        } catch (error: any) {
            logger.error('Erreur dans cancelCandidateResponse', error, { campaignToken });
            throw error;
        }
    }

    /**
     * Récupération du statut de réponse du candidat
     * @deprecated Use UnifiedCampaignService.getCampaignData instead
     */
    async getCandidateResponseStatus(token: string): Promise<CandidateResponseStatus> {
        logger.warn('getCandidateResponseStatus is deprecated - use UnifiedCampaignService.getCampaignData instead');
        logger.debug('Vérification statut candidat', { token });

        return await validateContextAndExecute(async () => {
            const { data, error } = await supabase
                .rpc('get_candidate_response_status', { p_campaign_token: token })
                .single();

            if (error) {
                logger.error('Erreur RPC get_candidate_response_status', error, { token });
                throw ErrorHandler.handleDatabaseError(error);
            }

            if (!data) {
                throw new ValidationError('Lien invalide ou expiré.');
            }

            logger.debug('Statut candidat récupéré', { token, status: data });
            return data as CandidateResponseStatus;
        }, 'campaign');
    }

    /**
     * NOUVEAU: Récupération unifiée de toutes les données du formulaire candidat
     * Remplace 4 appels séparés par 1 seul appel RPC optimisé
     * Élimine la race condition et améliore les performances
     */
    async getCandidateFormDataDirect(token: string): Promise<any> {
        logger.debug('🚀 Récupération unifiée données formulaire candidat', { token });

        const { data, error } = await supabase
            .rpc('get_candidate_form_data', { p_token: token })
            .single();

        if (error) {
            logger.error('Erreur RPC get_candidate_form_data', error, { token });
            throw ErrorHandler.handleDatabaseError(error);
        }

        if (!data) {
            throw new ValidationError('Lien invalide ou expiré.');
        }

        logger.debug('✅ Données formulaire candidat récupérées en une seule requête', data);
        return data;
    }

    /**
     * Récupération du statut de réponse du candidat sans validation de contexte
     * Version moderne qui ne dépend pas du système de contexte obsolète
     * OPTIMISÉ avec cache (TTL court car le statut peut changer)
     */
    async getCandidateResponseStatusDirect(token: string): Promise<CandidateResponseStatus> {
        logger.debug('Vérification statut candidat (direct + cache)', { token });

        // OPTIMISATION: Cache avec TTL court (2 minutes) car le statut peut changer
        const cachedStatus = performanceCache.get<CandidateResponseStatus>(CACHE_TYPES.CANDIDATE_STATUS, token);
        if (cachedStatus) {
            logger.debug('🚀 Statut candidat récupéré depuis le cache');
            return cachedStatus;
        }

        const { data, error } = await supabase
            .rpc('get_candidate_response_status', { p_campaign_token: token })
            .single();

        if (error) {
            logger.error('Erreur RPC get_candidate_response_status', error, { token });
            throw ErrorHandler.handleDatabaseError(error);
        }

        if (!data) {
            throw new ValidationError('Lien invalide ou expiré.');
        }

        const result = data as CandidateResponseStatus;

        // OPTIMISATION: Mettre en cache avec TTL court (2 minutes)
        performanceCache.set(CACHE_TYPES.CANDIDATE_STATUS, token, result, 2 * 60 * 1000);
        logger.debug('💾 Statut candidat mis en cache (TTL: 2min)');

        logger.debug('Statut candidat récupéré (direct)', { token, status: result });
        return result;
    }

    // Méthodes privées pour la logique interne

    private async validatePermissions(campaignId: string, userId?: string): Promise<void> {
        if (!userId) {
            // Pour les opérations publiques (via token), pas de validation de permission
            return;
        }

        const { data: campaign, error } = await supabase
            .from('campaigns')
            .select('recruiter_id')
            .eq('id', campaignId)
            .single();

        if (error) {
            throw ErrorHandler.handleDatabaseError(error);
        }

        // Vérifier que l'utilisateur est le propriétaire de la campagne
        if (campaign.recruiter_id !== userId) {
            // Ou vérifier s'il appartient à la même organisation
            const { data: userProfile, error: userError } = await supabase
                .from('profiles')
                .select('organization_id')
                .eq('id', userId)
                .single();

            if (!campaign.recruiter_id) {
                throw new PermissionDeniedError(
                    'modify_campaign',
                    'campaign',
                    userId
                );
            }

            const { data: campaignOwnerProfile, error: ownerError } = await supabase
                .from('profiles')
                .select('organization_id')
                .eq('id', campaign.recruiter_id)
                .single();

            if (userError || ownerError || !userProfile || !campaignOwnerProfile ||
                userProfile.organization_id !== campaignOwnerProfile.organization_id) {
                throw new PermissionDeniedError(
                    'modify_campaign',
                    'campaign',
                    userId
                );
            }
        }
    }

    private async validateBusinessRules(campaign: Campaign, newStatus: CampaignStatus): Promise<void> {
        const currentStatus = campaign.status as CampaignStatus;

        // Règle: Ne peut pas modifier une campagne terminée, annulée ou refusée
        if ([CampaignStatus.TERMINE, CampaignStatus.ANNULEE, CampaignStatus.REFUSE].includes(currentStatus)) {
            if (newStatus !== currentStatus) {
                throw new BusinessRuleViolationError(
                    'campaign_status_immutable',
                    'Impossible de modifier une campagne terminée, annulée ou refusée'
                );
            }
        }

        // Règle: Ne peut pas terminer une campagne sans référents complétés
        if (newStatus === CampaignStatus.TERMINE) {
            const hasCompletedReferents = await this.checkAllReferentsCompleted(campaign.id);
            if (!hasCompletedReferents) {
                throw new BusinessRuleViolationError(
                    'campaign_completion_requires_referents',
                    'Impossible de terminer une campagne sans que tous les référents aient complété leurs évaluations'
                );
            }
        }

        // Règle: Ne peut pas passer en attente de référents sans avoir de référents
        if (newStatus === CampaignStatus.EN_ATTENTE_REFERENTS) {
            const { data: referents, error } = await supabase
                .from('referents')
                .select('id')
                .eq('campaign_id', campaign.id);

            if (error) {
                throw ErrorHandler.handleDatabaseError(error);
            }

            if (!referents || referents.length === 0) {
                throw new BusinessRuleViolationError(
                    'campaign_requires_referents',
                    'Impossible de passer en attente de référents sans avoir ajouté de référents à la campagne'
                );
            }
        }

        // Règle: Ne peut pas activer une campagne sans critères d'évaluation
        if (newStatus === CampaignStatus.EN_ATTENTE_CANDIDAT && currentStatus === CampaignStatus.BROUILLON) {
            if (!campaign.evaluation_criteria || campaign.evaluation_criteria.length < 5) {
                throw new BusinessRuleViolationError(
                    'campaign_activation_requires_criteria',
                    'Impossible d\'activer une campagne sans au moins 5 critères d\'évaluation'
                );
            }

            if (!campaign.title || !campaign.description) {
                throw new BusinessRuleViolationError(
                    'campaign_activation_requires_info',
                    'Impossible d\'activer une campagne sans titre et description'
                );
            }
        }

        // Règle: Validation des transitions spécifiques selon l'état actuel
        if (currentStatus === CampaignStatus.EN_ATTENTE_CANDIDAT && newStatus === CampaignStatus.EN_ATTENTE_REFERENTS) {
            // Vérifier que le candidat a accepté
            const { data: candidateResponse, error: responseError } = await supabase
                .from('candidate_responses')
                .select('response_type')
                .eq('campaign_id', campaign.id)
                .single();

            if (responseError || !candidateResponse || candidateResponse.response_type !== 'accept') {
                throw new BusinessRuleViolationError(
                    'campaign_requires_candidate_acceptance',
                    'Impossible de passer en attente de référents sans acceptation du candidat'
                );
            }
        }
    }

    private async updateWithAudit(
        campaignId: string,
        updateData: CampaignUpdateData,
        reason?: string,
        userId?: string,
        previousStatus?: CampaignStatus
    ): Promise<Campaign> {
        const { data, error } = await supabase
            .from('campaigns')
            .update(updateData)
            .eq('id', campaignId)
            .select(`
        *,
        referents (*)
      `)
            .single();

        if (error) {
            throw ErrorHandler.handleDatabaseError(error);
        }

        // Logger la transition d'état si le statut a changé
        if (updateData.status && previousStatus && userId) {
            await this.auditService.logStateTransition(
                'campaign',
                campaignId,
                previousStatus,
                updateData.status,
                userId,
                reason
            );
        }

        return data as unknown as Campaign;
    }

    private async checkAllReferentsCompleted(campaignId: string): Promise<boolean> {
        // Essayer de récupérer depuis le cache d'abord
        const campaign = await this.getCampaign(campaignId);

        if (campaign.referents && campaign.referents.length > 0) {
            return campaign.referents.every(referent =>
                referent.status === ReferentStatus.COMPLETE
            );
        }

        // Fallback vers requête directe si pas de référents dans le cache
        const { data, error } = await supabase
            .from('referents')
            .select('status')
            .eq('campaign_id', campaignId);

        if (error) {
            throw ErrorHandler.handleDatabaseError(error);
        }

        if (!data || data.length === 0) {
            return false;
        }

        // Tous les référents doivent être au statut "Complété"
        return data.every(referent => referent.status === ReferentStatus.COMPLETE);
    }

    private async triggerStatusChangeEvents(campaign: Campaign, previousStatus: CampaignStatus): Promise<void> {
        const newStatus = campaign.status as CampaignStatus;

        logger.campaignAction('changement_statut', campaign.id, {
            previousStatus,
            newStatus,
            transition: `${previousStatus} → ${newStatus}`
        });

        // Ici on pourrait déclencher des notifications, emails, etc.
        // Pour l'instant, on se contente de logger

        if (newStatus === CampaignStatus.TERMINE) {
            logger.info('Campagne terminée, génération de rapport possible', { campaignId: campaign.id });
        }

        if (newStatus === CampaignStatus.REFUSE) {
            logger.info('Campagne refusée par le candidat', { campaignId: campaign.id });
        }
    }
}

// Instance singleton pour utilisation dans l'application
export const campaignService = new CampaignService();

// Exports pour compatibilité avec l'ancien code
export const getCandidateResponseStatus = (token: string) => campaignService.getCandidateResponseStatus(token);
export const fetchCampaignByToken = (token: string) => campaignService.getCampaignByToken(token);
export const acceptCampaign = (token: string) => campaignService.acceptCampaign(token);
export const refuseCampaign = (token: string, reason: string) => campaignService.refuseCampaign(token, reason);
export const cancelCandidateResponse = (token: string) => campaignService.cancelCandidateResponse(token);